import * as React from 'react'

import {
  UWPNotifier,
  OutputAction,
  DownloadStatus,
  AllDownloadsPayload,
} from '@shared'

// TODO(PVWinApp-394): Move to DVUI
import {
  DownloadButtonWithLabel,
  DownloadButton,
} from './DownloadButton'
import {
  withSpaContext,
  SpaContextProps,
} from '../SpaContext'

export type DownloadActionProps = {
  downloadInfo?: {
    downloadURL: string
    titleID: string
    episodeNumber: undefined | number
  }
  withLabel?: boolean
}

export type DownloadActionWithSpaContextProps = SpaContextProps & DownloadActionProps

type DownloadActionState = {
  downloadStatus: DownloadStatus
  downloadProgress: number
}

const getDownloadStatus = (titleID: string, downloads: AllDownloadsPayload) => {
  return titleID && downloads && downloads[titleID]
}

const getDownloadStateFromProps = (props: DownloadActionWithSpaContextProps) => {
  const downloadStatus = props.downloadInfo && getDownloadStatus(props.downloadInfo.titleID, props.downloads)

  return {
    downloadStatus: (downloadStatus && DownloadStatus[downloadStatus.Status]) || DownloadStatus.IDLE,
    downloadProgress: downloadStatus && downloadStatus.Progress || 0,
  }
}


export class DownloadActionWithSpaContext extends React.Component<
  DownloadActionWithSpaContextProps,
  DownloadActionState
> {

  state = getDownloadStateFromProps(this.props)

  startDownload = (e: React.MouseEvent) => {
    const { downloadInfo: {titleID}, saveMetaData } = this.props


    this.setState({
      downloadStatus: DownloadStatus.WAITING,
    })

    UWPNotifier.getInstance().publish(OutputAction.Download_Start, {
      titleId: titleID,
    })

    saveMetaData(titleID)

    e.preventDefault()
  }

  pauseDownload = (e: React.MouseEvent) => {
    UWPNotifier.getInstance().publish(OutputAction.Download_Pause, {
      titleId: this.props.downloadInfo.titleID,
    })
    e.preventDefault()
  }

  resumeDownload = (e: React.MouseEvent) => {
    UWPNotifier.getInstance().publish(OutputAction.Download_Resume, {
      titleId: this.props.downloadInfo.titleID,
    })
    e.preventDefault()
  }

  deleteDownload = (e: React.MouseEvent) => {
    UWPNotifier.getInstance().publish(OutputAction.Download_Delete, {
      titleId: this.props.downloadInfo.titleID,
    })
    e.preventDefault()
  }

  getDownloadAction = () => {
    switch (this.state.downloadStatus) {
      case DownloadStatus.IDLE:
        return this.startDownload
      case DownloadStatus.WAITING:
      case DownloadStatus.QUEUED:
      case DownloadStatus.DOWNLOADING:
        return this.cancelOrPause
      case DownloadStatus.PAUSED:
        return this.resumeDownload
      case DownloadStatus.ERROR:
        return this.resumeDownload
      case DownloadStatus.COMPLETED:
        return this.deleteDownload
      default:
        return this.startDownload
    }
  }

  cancelOrPause = (e: React.MouseEvent) => {
    // TODO(PVWinApp-384): Build a Cancel or Pause Modal
    UWPNotifier.getInstance().publish(OutputAction.Download_Pause, {
      titleId: this.props.downloadInfo.titleID,
    })
    e.preventDefault()
  }

  componentDidUpdate(prevProps: DownloadActionWithSpaContextProps) {
    const prevDownloadState = getDownloadStatus(prevProps.downloadInfo.titleID, prevProps.downloads)
    const prevStatus = prevDownloadState && prevDownloadState.Status
    const prevProgress = prevDownloadState && prevDownloadState.Progress

    const newDownloadState = getDownloadStatus(this.props.downloadInfo.titleID, this.props.downloads)
    const newStatus = newDownloadState && newDownloadState.Status
    const newProgress = newDownloadState && newDownloadState.Progress

    if (prevStatus !== newStatus || prevProgress !== newProgress) {
      const downloadState = getDownloadStateFromProps(this.props)
      this.setState({
        ...downloadState,
      })
    }
  }

  render() {
    const { downloadInfo, withLabel } = this.props

    if (!downloadInfo) {
      return null
    }

    const isEpisode = typeof downloadInfo.episodeNumber !== 'undefined'
    // TODO(PVWinApp-385): Localize Strings
    const episodeText = downloadInfo.episodeNumber === 0 ? 'Bonus Episode' : `Episode ${downloadInfo.episodeNumber}`

    // TODO(PVWinApp-385): Localize Strings
    const labelAction = {
      IDLE: 'Download',
      WAITING: 'Waiting',
      QUEUED: 'Queued',
      DOWNLOADING: 'Downloading',
      PAUSED: 'Resume',
      ERROR: 'Error',
      COMPLETED: 'Downloaded',
    }

    const labelActionForEpisodes = {
      IDLE: `Download ${episodeText}`,
      WAITING: 'Waiting',
      QUEUED: `Queued ${episodeText}`,
      DOWNLOADING: `Downloading ${episodeText}`,
      PAUSED: `Resume ${episodeText}`,
      ERROR: `Error downloading ${episodeText}`,
      COMPLETED: `Delete ${episodeText}`,
    }

    const label = isEpisode ? labelActionForEpisodes[this.state.downloadStatus] : labelAction[this.state.downloadStatus]

    const { downloadURL } = downloadInfo

    const downloadButtonProps = {
      label,
      downloadURL,
      status: this.state.downloadStatus,
      progress: this.state.downloadProgress,
      onClick: this.getDownloadAction(),
    }

    return (
      <div
        className="avu-full-width-phablet"
        data-automation-id="download-button"
      >
        {withLabel ? (
          <DownloadButtonWithLabel
            {...downloadButtonProps}
            forceDoubleHeight={true}
          />
        ) : (
          <DownloadButton {...downloadButtonProps} />
        )}
      </div>
    )
  }
}

export const DownloadAction = withSpaContext(DownloadActionWithSpaContext)
